<template>
  <div class="stats-screen">
    <div class="stats-header">
      <button class="icon-btn" title="Back to practice" aria-label="Back to practice" @click="goBack">
        <i class="fas fa-arrow-left"></i>
      </button>
      <h1 class="stats-title">Your practice</h1>
      <button class="icon-btn" title="Account" aria-label="Account" @click="openAccount">
        <i class="fas fa-user-circle"></i>
      </button>
      <button class="icon-btn" title="Settings" aria-label="Settings" @click="openSettings">
        <i class="fas fa-cog"></i>
      </button>
    </div>

    <div class="stats-content">
      <div class="stat-tiles">
        <div class="stat-tile">
          <span class="stat-value">{{ formatDuration(totalSeconds) }}</span>
          <span class="stat-label">Total practice</span>
        </div>
        <div class="stat-tile">
          <span class="stat-value">
            {{ streakDays }}<i v-if="streakDays > 0" class="fas fa-fire streak-icon"></i>
          </span>
          <span class="stat-label">Day streak</span>
        </div>
        <div class="stat-tile">
          <span class="stat-value">{{ sessionCount }}</span>
          <span class="stat-label">Sentences done</span>
        </div>
        <div class="stat-tile">
          <span class="stat-value">{{ avgAccuracy7d !== null ? avgAccuracy7d + '%' : '-' }}</span>
          <span class="stat-label">Avg score, 7 days</span>
        </div>
      </div>

      <div v-if="sessionCount === 0" class="empty-state">
        <i class="fas fa-microphone-alt"></i>
        <p>No practice recorded yet.</p>
        <p class="empty-hint">Practice a sentence on the <strong>Practice</strong> screen and it will show up here.</p>
        <button class="btn btn-primary" @click="goBack">Start practicing</button>
      </div>

      <template v-else>
        <h4 class="section-title">Clips practiced</h4>

        <div class="clips-list">
          <div
            v-for="video in shownVideos"
            :key="video.videoId"
            class="video-item"
            :class="{ 'video-item--open': expandedVideos[video.videoId] }"
          >
            <div class="video-item-hdr" @click="toggleVideo(video.videoId)">
              <img
                class="v-thumb"
                :src="thumbnailUrl(video.videoId)"
                alt=""
                loading="lazy"
              >
              <div class="v-info">
                <div class="v-title">{{ titleFor(video.videoId) }}</div>
                <div class="v-meta">
                  {{ video.sentences.length }} sentence{{ video.sentences.length === 1 ? '' : 's' }}
                  · {{ formatDate(video.lastPracticedAt) }}
                </div>
              </div>
              <button
                type="button"
                class="icon-btn play-btn"
                :title="'Practice ' + titleFor(video.videoId)"
                :aria-label="'Practice ' + titleFor(video.videoId)"
                @click.stop="resumeVideo(video)"
              ><i class="fas fa-play"></i></button>
              <i class="fas fa-chevron-down v-chevron"></i>
            </div>

            <div class="sentences-wrap">
              <div class="sentences-inner">
                <div
                  v-for="(sentence, sIdx) in video.sentences"
                  :key="sentence.sentenceKey"
                  class="sentence-item"
                  :class="{ 'sentence-item--open': expandedSentences[sentence.sentenceKey] }"
                >
                  <!-- Two lines: six controls don't fit one row on a 390px phone. -->
                  <div class="sentence-hdr" @click="toggleSentence(sentence.sentenceKey)">
                    <span class="s-seq" :title="'Sentence ' + (sIdx + 1) + ' of the ones you practiced in this clip'">{{ sIdx + 1 }}</span>

                    <div class="s-body">
                      <div class="s-line">
                        <span class="s-ts">{{ formatClock(sentence.segStart) }}</span>
                        <span class="score-pill" :class="badgeClass(sentence.lastAccuracy)">{{ sentence.lastAccuracy }}%</span>
                        <div class="attempt-bars" :aria-label="attemptsLabel(sentence)">
                          <span
                            v-for="(attempt, i) in sentence.attempts"
                            :key="i"
                            class="attempt-bar"
                            :class="'attempt-bar--' + tierFor(attempt.accuracy)"
                            :style="{ height: barHeight(attempt.accuracy) }"
                            :title="attempt.accuracy + '%'"
                          ></span>
                        </div>
                      </div>
                      <span class="s-meta">
                        {{ sentence.attemptCount }} attempt{{ sentence.attemptCount === 1 ? '' : 's' }}<template v-if="sentence.delta !== 0"> · {{ sentence.delta > 0 ? '+' : '' }}{{ sentence.delta }} pts</template>
                      </span>
                    </div>

                    <button
                      type="button"
                      class="icon-btn icon-btn-sm play-btn"
                      :title="'Practice this sentence at ' + formatClock(sentence.segStart)"
                      :aria-label="'Practice this sentence at ' + formatClock(sentence.segStart)"
                      @click.stop="resume(video.videoId, sentence.segStart)"
                    ><i class="fas fa-play"></i></button>
                    <i class="fas fa-chevron-down s-chevron"></i>
                  </div>

                  <div class="attempts-wrap">
                    <div class="attempts-inner">
                      <div
                        v-for="(attempt, i) in sentence.attempts"
                        :key="i"
                        class="attempt-row"
                      >
                        <span
                          class="attempt-dot"
                          :class="'attempt-dot--' + tierFor(attempt.accuracy)"
                        ></span>
                        <span class="attempt-date">
                          {{ formatDate(attempt.practicedAt) }}{{ attempt.durationSec ? ' · ' + formatDuration(attempt.durationSec) : '' }}
                        </span>
                        <span v-if="i === sentence.attempts.length - 1" class="latest-tag">Latest</span>
                        <span
                          class="attempt-score"
                          :class="'attempt-score--' + tierFor(attempt.accuracy)"
                        >{{ attempt.accuracy }}%</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="byVideo.length > clipsShown" class="clips-more">
          <button type="button" class="btn btn-secondary" @click="showMoreClips">
            Show more clips ({{ byVideo.length - clipsShown }} left)
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { getVideoTitle } from '../services/videoinfo';

const CLIPS_PAGE = 10;

export default {
  name: 'StatsScreen',

  data() {
    return {
      videoTitles: {},
      expandedVideos: {},
      expandedSentences: {},
      clipsShown: CLIPS_PAGE
    };
  },

  computed: {
    ...mapState('stats', ['totalSeconds', 'streakDays', 'sessionCount', 'byVideo']),
    shownVideos() {
      return this.byVideo.slice(0, this.clipsShown);
    },
    avgAccuracy7d() {
      return typeof this.$store.state.stats.avgAccuracy7d === 'number'
        ? this.$store.state.stats.avgAccuracy7d
        : null;
    }
  },

  mounted() {
    this.$store.dispatch('stats/refreshStats');
    this.loadTitles();
    if (window.$analytics) {
      window.$analytics.trackEvent('stats_viewed', { category: 'Stats' });
    }
  },

  methods: {
    goBack() {
      this.$router.push('/practice').catch(function() {});
    },

    openSettings() {
      this.$store.dispatch('settings/openSettings');
    },

    openAccount() {
      this.$store.dispatch('settings/openSettings', 'account');
    },

    // `t` unrounded: flooring a 13.4s start can land before the previous
    // sentence's end.
    resume(videoId, segStart) {
      this.$router.push({
        path: '/practice',
        query: { v: videoId, t: String(segStart || 0) }
      }).catch(function() {});
    },

    // Earliest practiced sentence in the clip.
    resumeVideo(video) {
      const first = video.sentences[0];
      this.resume(video.videoId, first ? first.segStart : 0);
    },

    showMoreClips() {
      this.clipsShown += CLIPS_PAGE;
      this.loadTitles();
    },

    async loadTitles() {
      const ids = new Set(this.shownVideos.map(v => v.videoId));
      for (const videoId of ids) {
        if (this.videoTitles[videoId]) continue;
        const title = await getVideoTitle(videoId);
        if (title) this.$set(this.videoTitles, videoId, title);
      }
    },

    titleFor(videoId) {
      return this.videoTitles[videoId] || videoId;
    },

    thumbnailUrl(videoId) {
      return `https://i.ytimg.com/vi/${videoId}/mqdefault.jpg`;
    },

    toggleVideo(videoId) {
      this.$set(this.expandedVideos, videoId, !this.expandedVideos[videoId]);
    },

    toggleSentence(sentenceKey) {
      this.$set(this.expandedSentences, sentenceKey, !this.expandedSentences[sentenceKey]);
    },

    tierFor(accuracy) {
      if (accuracy >= 85) return 'good';
      if (accuracy >= 60) return 'ok';
      return 'poor';
    },

    badgeClass(accuracy) {
      return 'score-pill--' + this.tierFor(accuracy);
    },

    barHeight(accuracy) {
      return `${Math.max(4, Math.round((accuracy / 100) * 22))}px`;
    },

    attemptsLabel(sentence) {
      return `Attempts, oldest first: ${sentence.attempts.map(a => `${a.accuracy}%`).join(', ')}`;
    },

    formatDuration(seconds) {
      const total = Math.round(seconds);
      if (total >= 3600) return `${Math.floor(total / 3600)}h ${Math.floor((total % 3600) / 60)}m`;
      if (total >= 60)   return `${Math.floor(total / 60)}m ${total % 60}s`;
      return `${total}s`;
    },

    formatClock(time) {
      const t = Math.floor(time || 0);
      const minutes = Math.floor(t / 60);
      const seconds = String(t % 60).padStart(2, '0');
      return `${minutes}:${seconds}`;
    },

    formatDate(iso) {
      try {
        return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      } catch (e) {
        return '';
      }
    }
  }
};
</script>

<style scoped>
.stats-screen {
  height: 100vh;
  height: 100dvh;
  display: flex;
  flex-direction: column;
  background: var(--canvas);
  max-width: 800px;
  margin: 0 auto;
  overflow: hidden;
}

.stats-header {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 56px;
  padding: 0 8px;
  background: var(--chrome);
  border-bottom: 1px solid var(--line);
  flex: none;
}

.stats-title {
  flex: 1;
  margin: 0 0 0 6px;
  font-size: 17px;
  font-weight: 700;
  color: var(--text);
}

.stats-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.stat-tiles {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 24px;
}

.stat-tile {
  background: var(--surface);
  border-radius: var(--r-card);
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-value {
  font-size: 22px;
  font-weight: 800;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  display: flex;
  align-items: center;
  gap: 6px;
}

.streak-icon {
  font-size: 15px;
  color: var(--accent);
}

.stat-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--muted);
}

.section-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--faint);
  margin: 0 0 10px;
}

.clips-more {
  display: flex;
  justify-content: center;
  margin-top: 12px;
}

.clips-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.video-item {
  border-radius: var(--r-card);
  background: var(--surface);
  border: 1px solid transparent;
  overflow: hidden;
  transition: border-color 0.15s;
}

.video-item--open {
  border-color: var(--line-strong);
}

.video-item-hdr {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  cursor: pointer;
  user-select: none;
}

.v-thumb {
  width: 64px;
  height: 36px;
  border-radius: 8px;
  object-fit: cover;
  background: var(--field);
  flex: none;
}

.v-info {
  flex: 1;
  min-width: 0;
}

.v-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v-meta {
  font-size: 12px;
  color: var(--muted);
  margin-top: 2px;
}

/* Separate from the accordion toggle: one target can't mean both. The play
   glyph is nudged right (a triangle's optical centre sits left). */
.play-btn {
  background: var(--accent-soft);
  color: var(--accent);
}
.play-btn i { transform: translateX(1px); }
.play-btn:hover {
  background: var(--accent);
  color: var(--accent-ink);
}

.v-chevron,
.s-chevron {
  width: 16px;
  text-align: center;
  font-size: 11px;
  color: var(--faint);
  transition: transform 0.2s;
  flex: none;
}

.video-item--open .v-chevron,
.sentence-item--open .s-chevron {
  transform: rotate(180deg);
}

.sentences-wrap {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.25s ease;
}

.video-item--open .sentences-wrap {
  max-height: 9999px;
  border-top: 1px solid var(--line);
}

.sentences-inner {
  display: flex;
  flex-direction: column;
}

.sentence-item {
  border-bottom: 1px solid var(--line);
  overflow: hidden;
}

.sentence-item:last-child {
  border-bottom: none;
}

.sentence-hdr {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  cursor: pointer;
}

/* Ordinal within this list: history stores timestamps, not transcript indices. */
.s-seq {
  flex: none;
  box-sizing: border-box;
  min-width: 24px;
  height: 24px;
  padding: 0 6px;
  border-radius: 8px;
  background: var(--field);
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sentence-item--open .s-seq {
  background: var(--accent-soft);
  color: var(--accent);
}

.s-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.s-line {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.s-ts {
  font-size: 13px;
  font-weight: 700;
  color: var(--text);
  font-variant-numeric: tabular-nums;
  flex: none;
}

.s-meta {
  font-size: 12px;
  color: var(--muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* First thing to go when the row runs out of room. */
.attempt-bars {
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 20px;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
}

.attempt-bar {
  width: 5px;
  border-radius: 2px 2px 0 0;
  flex: none;
}

.attempt-bar--good { background: var(--pass); }
.attempt-bar--ok   { background: var(--warn); }
.attempt-bar--poor { background: var(--fail); }

.score-pill {
  font-size: 12px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: var(--r-pill);
  flex: none;
  font-variant-numeric: tabular-nums;
}

.score-pill--good { color: var(--pass); background: var(--pass-soft); }
.score-pill--ok   { color: var(--warn); background: var(--warn-soft); }
.score-pill--poor { color: var(--fail); background: var(--fail-soft); }

.attempts-wrap {
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.2s ease;
}

.sentence-item--open .attempts-wrap {
  max-height: 9999px;
}

.attempts-inner {
  padding: 0 12px 12px 48px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.attempt-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: var(--r-control);
  background: var(--surface-2);
}

.attempt-dot {
  width: 6px;
  height: 6px;
  border-radius: var(--r-pill);
  flex: none;
}

.attempt-dot--good { background: var(--pass); }
.attempt-dot--ok   { background: var(--warn); }
.attempt-dot--poor { background: var(--fail); }

.attempt-date {
  font-size: 12px;
  color: var(--muted);
  flex: 1;
}

.latest-tag {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--accent);
  background: var(--accent-soft);
  padding: 2px 6px;
  border-radius: var(--r-pill);
}

.attempt-score {
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.attempt-score--good { color: var(--pass); }
.attempt-score--ok   { color: var(--warn); }
.attempt-score--poor { color: var(--fail); }

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  padding: 40px 24px;
  background: var(--surface);
  border-radius: var(--r-card);
  color: var(--text);
}

.empty-state i {
  font-size: 28px;
  color: var(--faint);
}

.empty-state p { margin: 0; }

.empty-hint {
  font-size: 14px;
  color: var(--muted);
  max-width: 34ch;
}

.empty-state .btn { margin-top: 12px; }

@media (max-width: 560px) {
  .stat-tiles { grid-template-columns: repeat(2, 1fr); }
}

/* Narrow phones: drop the sparkline so controls keep their targets. */
@media (max-width: 400px) {
  .attempt-bars { display: none; }
  .sentence-hdr { gap: 8px; }
  .attempts-inner { padding-left: 12px; }
}
</style>
